import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import type { ProjectAdoptions } from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  SUMMARY_STRUCTURE_CLIENT_VERSION,
  classifySummaryStructureFailure,
  useSummaryStructureQuery,
  type SummaryStructureFailure,
  type SummaryStructureRegistry,
} from "@/api/summaryStructure";
import { ErrorState, StaleDataNotice, UnavailableState } from "@/ui/states";

import { Identity } from "./SummaryIdentity";
import { SummaryLayerMatrix } from "./SummaryLayerMatrix";
import { SummaryPresentationControls } from "./SummaryPresentationControls";
import type { SummaryPromptRef } from "@/lib/summaryStructure";

import { SummaryReportStructure } from "./SummaryReportStructure";

/**
 * The Summary report structure, on the project's own Summary route (#640).
 *
 * ## What it adds to this screen
 *
 * The Clinical Summary already carries what this project ADOPTS and what its
 * effective projection contains. What no surface said until now is which of the
 * ratified contract's three Summary layers any of it belongs to — and that is
 * the distinction the contract's Summary row exists to protect: "a UI form or a
 * prompt blob is not the runtime clinical record". This panel renders #639's
 * server-owned registry: the three layers and who may write each, the twelve
 * report sections and what each maps to in the runtime projection, and the
 * presentation fields the rendering layer declares.
 *
 * ## Registry read plus Project presentation state
 *
 * It reads the registry and the dedicated Project presentation document. The
 * project's pin set is the Clinical Summary's single `useProjectAdoptionsQuery`
 * passed down, on the arrangement #373 established so that the views on this
 * page cannot disagree about one set; this panel joins it and never re-reads
 * it. The only mutation replaces the complete renderer document through the
 * presentation layer's own compare-and-set route. Its generated request type
 * cannot represent a prompt pin or runtime clinical value.
 *
 * ## Failure never removes the identity
 *
 * Whatever goes wrong, the registry's exact identity stays on screen: the
 * version this build renders, the version the deployment serves when it said
 * so, and — on success — the canonical digest that makes the structure citable
 * rather than merely labelled. A structure panel that failed silently, or that
 * failed and took the version with it, would leave a reader unable to say WHICH
 * structure they were denied. Each failure is stated as itself:
 *
 *  - **409 `summary_structure_version_mismatch`** — this deployment serves a
 *    structure this build was not written against. Terminal by contract
 *    ("there is nothing to resend"), so no retry is offered and no cached
 *    document is shown: `queryClient.ts` lists the code among the terminal
 *    conflicts for exactly this reason.
 *  - **501** — the registry is not built in this deployment. Terminal.
 *  - **401/403** — the session does not hold `library.read`, which is the
 *    capability the registry takes because structure policy is library policy.
 *  - **502/503/504** — the service could not build the document; the one arm
 *    that offers a retry.
 *
 * There is deliberately no empty state. The contract promises three layers,
 * twelve sections and five value categories and says "never a subset", so an
 * empty document is not a real, successful, empty answer the way an empty
 * projection is — it is a response that is not this contract, and it reaches
 * the unclassified failure arm rather than being rendered as "nothing here".
 */
export function SummaryStructure({
  adoptions,
  summaryPrompts,
  projectId,
}: {
  /** The Clinical Summary's single pin-set query. Not re-fetched here. */
  adoptions: UseQueryResult<ProjectAdoptions>;
  /**
   * The runtime projection's own statement of which exact pins supplied this
   * project's Summary, from the page's single section query. `undefined`
   * while unread — the report structure then claims no effective source.
   */
  summaryPrompts?: readonly SummaryPromptRef[] | undefined;
  projectId: string;
}) {
  const registryQuery = useSummaryStructureQuery();

  // The repository's cache policy, stated once for this panel. TanStack Query
  // keeps `data` when a REFETCH fails, so a document read successfully and then
  // refused would keep rendering from `.data` — which here is a claim about who
  // owns a clinical value. `queryClient.ts` draws the line: a transient outage
  // may show the last success with a stale notice; 401/403/404/501 and a
  // terminal 409 fail closed. A version mismatch is the sharpest case and is
  // listed among those terminal 409s, because the structure this build rendered
  // a moment ago is precisely the one the deployment has stopped stating.
  const terminalFailure =
    registryQuery.isError && isTerminalQueryError(registryQuery.error);
  const readableRegistry = terminalFailure ? undefined : registryQuery.data;

  if (registryQuery.isPending) {
    return (
      <div className="space-y-4">
        <RegistryIdentity reading />
        <QueryPendingState
          isPaused={registryQuery.isPaused}
          failureReason={registryQuery.failureReason}
          label="Loading the Summary report structure"
          errorTitle={FAILURE_TITLE.error}
          rows={4}
          queryKey={queryKeys.librarySummaryStructure}
        />
      </div>
    );
  }

  if (readableRegistry === undefined) {
    const failure = classifySummaryStructureFailure(registryQuery.error);
    return (
      <div className="space-y-4">
        <RegistryIdentity failure={failure} />
        <StructureFailure
          failure={failure}
          error={registryQuery.error}
          onRetry={() => void registryQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RegistryIdentity registry={readableRegistry} />

      {/*
        A refresh that failed after a success keeps the last document on screen
        with the repository's standard stale notice — and reaches here only when
        the failure was transient, because a terminal one left
        `readableRegistry` undefined above.
      */}
      {registryQuery.isError ? (
        <StaleDataNotice
          updatedAt={registryQuery.dataUpdatedAt}
          onRetry={() => void registryQuery.refetch()}
        />
      ) : null}

      <SummaryLayerMatrix registry={readableRegistry} />
      <SummaryReportStructure
        registry={readableRegistry}
        adoptions={adoptions.isSuccess ? adoptions.data : undefined}
        summaryPrompts={summaryPrompts}
      />
      <SummaryPresentationControls
        registry={readableRegistry}
        projectId={projectId}
      />
    </div>
  );
}

/**
 * The registry's exact identity, rendered in every state this panel has.
 *
 * On success it is the served document's own `registryId`, `version`,
 * `authority` and canonical `digest` — the digest is what makes downstream
 * evidence able to bind to an exact structure rather than to a version label,
 * so it is shown rather than kept for logs. Truncated to twelve characters for
 * a strip that has to stay compact, with the whole value on the element's
 * `title`, so it stays recoverable by hover and by selection rather than being
 * lost to the abbreviation.
 *
 * Before and after success it is what the browser can still vouch for: the
 * structure version THIS build renders, plus the served version whenever the
 * refusal named one. That pairing is the whole content of a mismatch, and
 * dropping either half would leave a reader with "something is wrong with the
 * structure" and no way to say which two structures disagreed.
 */
function RegistryIdentity({
  registry,
  failure,
  reading = false,
}: {
  registry?: SummaryStructureRegistry;
  failure?: SummaryStructureFailure;
  /**
   * Set while the read is still in flight.
   *
   * Without it the served-version cell would say "Not reported" during every
   * load, which is a statement about what the deployment answered — and it has
   * not answered yet. The two are different facts and the strip says which one
   * it is holding.
   */
  reading?: boolean;
}) {
  const servedVersion =
    registry?.version ??
    (failure?.kind === "mismatch" ? failure.servedVersion : null);

  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-secondary/40 px-4 py-3 text-xs">
      <Fact label="Registry">
        <Identity>
          {registry?.registryId ?? "summary_report_structure"}
        </Identity>
      </Fact>
      <Fact label="Rendered by this build">
        <Identity>{SUMMARY_STRUCTURE_CLIENT_VERSION}</Identity>
      </Fact>
      <Fact label="Served by this deployment">
        {servedVersion !== null ? (
          <Identity>{servedVersion}</Identity>
        ) : (
          <span className="text-muted-foreground">
            {reading ? "Reading…" : "Not reported"}
          </span>
        )}
      </Fact>
      {registry ? (
        <>
          <Fact label="Structure digest">
            <Identity title={registry.digest}>
              {registry.digest.slice(0, 12)}…
            </Identity>
          </Fact>
          <Fact label="Authority">
            {/*
              `authority` is a one-member literal in the contract — "clients
              discover them and never author or submit them" — so this cell is
              a statement about who owns the document, not a value that varies.
            */}
            Server-owned
          </Fact>
        </>
      ) : null}
    </dl>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}

/**
 * One heading per failure kind.
 *
 * `satisfies Record<SummaryStructureFailure["kind"], string>` is the
 * load-bearing part, exactly as in `SummaryContent`: a kind added to the union
 * without a heading here stops the build, which is what keeps a new refusal
 * from rendering as a blank structure panel.
 */
const FAILURE_TITLE = {
  mismatch: "This deployment serves a different Summary report structure",
  unavailable:
    "The Summary report structure is not available in this deployment",
  denied: "The Summary report structure could not be loaded",
  upstream: "The Editor Service could not build the Summary report structure",
  error: "The Summary report structure could not be loaded",
} satisfies Record<SummaryStructureFailure["kind"], string>;

function StructureFailure({
  failure,
  error,
  onRetry,
}: {
  failure: SummaryStructureFailure;
  error: unknown;
  onRetry: () => void;
}) {
  switch (failure.kind) {
    case "mismatch":
      return (
        <ErrorState
          title={FAILURE_TITLE.mismatch}
          description={
            failure.servedVersion === null
              ? `This browser build renders Summary report structure ${SUMMARY_STRUCTURE_CLIENT_VERSION} and asked the Editor Service to confirm it. The service refused because it serves a different one, and did not report which. Nothing is shown in its place: rendering ${SUMMARY_STRUCTURE_CLIENT_VERSION}'s layers and mappings against another structure would state ownership this deployment never published. Asking again cannot change the answer — the fix is a browser build matching this deployment. Everything else on this screen is unaffected.`
              : `This browser build renders Summary report structure ${SUMMARY_STRUCTURE_CLIENT_VERSION}; this deployment serves ${failure.servedVersion}. Nothing is shown in its place: rendering ${SUMMARY_STRUCTURE_CLIENT_VERSION}'s layers and mappings against ${failure.servedVersion} would state ownership this deployment never published. Asking again cannot change the answer — the fix is a browser build matching this deployment. Everything else on this screen is unaffected.`
          }
        />
      );
    case "unavailable":
      return (
        <UnavailableState
          title={FAILURE_TITLE.unavailable}
          description="The Editor Service accepts this request but has not implemented the Summary report-structure registry here, so there is nothing to show. It resolves when the endpoint is deployed — there is nothing to retry, and no part of the structure is being shown from anywhere else."
          blockedBy="HTTP 501 not_implemented — Editor Service endpoint not built in this deployment."
        />
      );
    case "upstream":
      return (
        <ErrorState
          title={FAILURE_TITLE.upstream}
          description="Building the structure needs the packaged seed catalog, and it is unavailable right now. The document fails closed rather than dropping the runtime coordinates it could not prove, so nothing partial is shown. This one is worth asking again."
          onRetry={onRetry}
        />
      );
    // 401, 403 and everything unclassified keep the shared vocabulary, which
    // already renders each correctly and decides the retry from
    // `isTerminalQueryError`.
    case "denied":
    case "error":
      return (
        <QueryErrorState
          error={error}
          subject="Summary report structure"
          title={FAILURE_TITLE.denied}
          description="Nothing is shown in place of it. An empty structure here would read as a Summary with no sections and no layers, which is not what the Editor Service reported."
          // A denial gets its own sentence rather than the transient one:
          // `QueryErrorState` renders `DeniedState` for a 403 and ignores
          // `description` there, so a surface that passes only the latter has
          // no copy on the arm a clinician is most likely to meet. The
          // capability is named because it is not the obvious one — the
          // registry takes `library.read`, on the reasoning that structure
          // policy IS clinical-library policy and reading it is not a project
          // read, so a session that can open this project may still be refused.
          deniedDescription="Reading the Summary report structure needs the clinical-library read permission — structure policy is library policy, not a project read — and this session does not hold it. Nothing is shown in its place, and the rest of this screen is unaffected."
          onRetry={onRetry}
        />
      );
  }
}
